import { all, put, select, takeEvery } from 'redux-saga/effects';
import DataTypes from './DataTypes';
import createRequest from '../../api/httpRequest';
import API from '../../api/data';
import NotificationTypes from '../notification/NotificationTypes';
import snakecaseKeys from 'snakecase-keys';
import Util from '../../utils/Util';
import { saveAs } from 'file-saver';
import { getCompanyDomain } from '../company/companySelector';
import ErrorTypes from '../handler/ErrorTypes';
import {SECTION_TYPES, UPDATE_DATA_PROGRESS_STATUS} from '../../configs/AppConfig';
import UploadTypes from "../upload/UploadTypes";

function * loadDisclosureData () {
  yield takeEvery(DataTypes.GET_DISCLOSURE_DATA_REQUEST, _loadDisclosureData);
}

function * _loadDisclosureData ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const request = snakecaseKeys({ ...payload, domain });
  yield createRequest({
    api: {
      ...API.GET_DISCLOSURE_DATA,
      params: {
        ...request,
        is_preview: request.is_preview === undefined || request.is_preview === null ? true : request.is_preview
      }
    },
    onSuccess: function * (response) {
      yield put({
        type: DataTypes.UPDATE_DISCLOSURE_QUERIES,
        payload: { ...payload }
      });
      yield put({
        type: DataTypes.GET_DISCLOSURE_DATA_SUCCESS,
        payload: Util.toCamelCaseKey(response.data)
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: DataTypes.GET_DISCLOSURE_DATA_ERROR,
        payload: status
      });
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    },
    onRequestError: function * () {
      yield put({
        type: DataTypes.UPDATE_DISCLOSURE_QUERIES,
        payload: payload
      });
    }
  });
}

function * loadPatientData () {
  yield takeEvery(DataTypes.GET_PATIENT_DATA_REQUEST, _loadPatientData);
}

function * _loadPatientData ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const request = snakecaseKeys({ ...payload, domain });
  yield createRequest({
    api: {
      ...API.GET_DISCLOSURE_DATA,
      params: {
        ...request,
        is_preview: request.is_preview === undefined || request.is_preview === null ? true : request.is_preview
      }
    },
    onSuccess: function * (response) {
      yield put({
        type: DataTypes.UPDATE_PATIENT_QUERIES,
        payload: { ...payload }
      });
      yield put({
        type: DataTypes.GET_PATIENT_DATA_SUCCESS,
        payload: Util.toCamelCaseKey(response.data)
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: DataTypes.GET_PATIENT_DATA_ERROR,
        payload: status
      });
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    },
    onRequestError: function * () {
      yield put({
        type: DataTypes.UPDATE_DISCLOSURE_QUERIES,
        payload: payload
      });
    }
  });
}

function * loadRecordDetail () {
  yield takeEvery(DataTypes.GET_DATA_RECORD_DETAIL_REQUEST, _loadRecordDetail);
}

function * _loadRecordDetail ({ payload }) {
  const api = {
    ...API.GET_DISCLOSURE_DETAIL,
    url: API.GET_DISCLOSURE_DETAIL.url + payload
  };
  yield createRequest({
    api: api,
    onSuccess: function * (data) {
      yield put({
        type: DataTypes.GET_DATA_RECORD_DETAIL_SUCCESS,
        payload: Util.toCamelCaseKey(data.data)
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: DataTypes.GET_DATA_RECORD_DETAIL_ERROR,
        payload: status
      });
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.error.${status}`,
            level: 'error',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
    }
  });
}

function * loadYears () {
  yield takeEvery(DataTypes.GET_YEARS_REQUEST, _loadYears);
}

function * _loadYears ({ payload }) {
  const domain = yield select(getCompanyDomain);
  const request = snakecaseKeys({ ...payload, domain });
  yield createRequest({
    api: {
      ...API.GET_YEARS_LIST,
      params: request
    },
    onSuccess: function * (response) {
      yield put({
        type: DataTypes.GET_YEARS_SUCCESS,
        payload: {
          type: payload.sectionType,
          data: response.data.sort(Util.sortDesc)
        }
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: DataTypes.GET_YEARS_ERROR,
        payload: status
      });
    }
  });
}

function * loadRootSections () {
  yield takeEvery(DataTypes.GET_ROOT_SECTIONS_REQUEST, _loadRootSections);
}

function * _loadRootSections ({ payload }) {
  const request = snakecaseKeys({ ...payload });
  yield createRequest({
    api: {
      ...API.GET_ROOT_SECTIONS,
      params: request
    },
    onSuccess: function * (response) {
      yield put({
        type: DataTypes.GET_ROOT_SECTIONS_SUCCESS,
        payload: {
          data: response.data,
          type: payload.sectionType
        }
      });
    },
    onError: function * ({ status }) {
      yield put({
        type: DataTypes.GET_ROOT_SECTIONS_ERROR,
        payload: status
      });
    }
  });
}

function * exportData () {
  yield takeEvery(DataTypes.EXPORT_DATA_REQUEST, _exportData);
}

function * _exportData ({ payload }) {
  const request = snakecaseKeys(payload);
  yield createRequest({
    api: {
      ...API.EXPORT_DATA,
      params: request
    },
    onSuccess: function (response) {
      // Set '\ufeff' so that the file is actually converted to UTF-8
      const blob = new Blob(['\ufeff', response], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, payload.fileName);
    }
  });
}

function * downloadPDF () {
  yield takeEvery(DataTypes.DOWNLOAD_DATA_REQUEST, _downloadPDF);
}

function * _downloadPDF ({ payload }) {
  const domain = yield select(getCompanyDomain);
  yield createRequest({
    api: {
      ...API.DOWNLOAD_PDF,
      params: snakecaseKeys({ ...payload, domain: domain }),
      responseType: 'blob'
    },
    onSuccess: function (response) {
      const blob = new Blob([response], { type: 'application/pdf;charset=utf-8' });
      saveAs(blob, payload.fileName);
    },
    onRequestError: function * (error) {
      if (error.status === 404) {
        yield put({
          type: NotificationTypes.SHOW_NOTIFICATION,
          payload: {
            config: {
              message: 'api.error.405',
              level: 'error'
            }
          }
        });
      } else {
        yield put({ type: ErrorTypes.API_RESPONSE_ERROR, response: error });
      }
    }
  });
}

function * deleteDataRecord () {
  yield takeEvery(DataTypes.DELETE_DATA_RECORD_REQUEST, _deleteDataRecord);
}

function * _deleteDataRecord ({ payload }) {
  const api = { ...API.DELETE_DATA_RECORD };
  api.url = api.url + payload.id;
  yield createRequest({
    api: { ...api },
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.delete.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: payload.type === SECTION_TYPES.DISCLOSURE.type ? DataTypes.GET_DISCLOSURE_DATA_REQUEST : DataTypes.GET_PATIENT_DATA_REQUEST,
        payload: payload.queries
      });
    }
  });
}

function * updateDataRecord () {
  yield takeEvery(DataTypes.UPDATE_DATA_RECORD_REQUEST, _updateDataRecord);
}

function * _updateDataRecord ({ payload }) {
  yield put({
    type: DataTypes.UPDATE_LOADING,
    payload: true
  });
  const api = {
    ...API.UPDATE_DATA_RECORD,
    params: payload.params
  };
  yield createRequest({
    api: api,
    onSuccess: function * () {
      yield put({
        type: payload.type === SECTION_TYPES.DISCLOSURE.type ? DataTypes.GET_DISCLOSURE_DATA_REQUEST : DataTypes.GET_PATIENT_DATA_REQUEST,
        payload: payload.queries
      });
      yield put({
        type: DataTypes.UPDATE_LOADING,
        payload: false
      });
      yield put({
        type: DataTypes.UPDATE_DATA_RECORD_PROGRESS,
        payload: UPDATE_DATA_PROGRESS_STATUS.PROGRESS
      });
    }
  });
}

function * checkUpdateDataRecordProgress () {
  yield takeEvery(DataTypes.CHECK_UPDATE_DATA_RECORD_PROGRESS_REQUEST, _checkUpdateDataRecordProgress);
}

function * _checkUpdateDataRecordProgress ({ payload }) {
  yield createRequest({
    api: {
      ...API.CHECK_UPDATE_DATA_RECORD_PROGRESS,
      params: snakecaseKeys(payload)
    },
    onSuccess: function * (response) {
      yield put({
        type: DataTypes.CHECK_UPDATE_DATA_RECORD_PROGRESS_SUCCESS,
        payload: Util.toCamelCaseKey(response.data)
      });
    }
  });
}

function * moveDataRecordToDataRecordPreview () {
  yield takeEvery(DataTypes.UPDATE_DATA_RECORD_TO_PREVIEW, _moveDataRecordToDataRecordPreview);
}

function * _moveDataRecordToDataRecordPreview ({ payload }) {
  yield put({
    type: DataTypes.UPDATE_LOADING,
    payload: { isLoading: true, companyId: payload.company_id, type: 'moveData' }
  });
  const api = {
    ...API.UPDATE_DATA_RECORD_TO_PREVIEW,
    params: payload
  };
  yield createRequest({
    api: api,
    onSuccess: function * ({ status }) {
      yield put({
        type: NotificationTypes.SHOW_NOTIFICATION,
        payload: {
          config: {
            message: `api.update.${status}`,
            level: 'success',
            autoDismiss: 3,
            position: 'tc'
          }
        }
      });
      yield put({
        type: DataTypes.UPDATE_LOADING,
        payload: null
      });
    }
  });
}

export default function * dataSaga () {
  yield all([
    loadDisclosureData(),
    loadPatientData(),
    loadRecordDetail(),
    loadYears(),
    loadRootSections(),
    exportData(),
    downloadPDF(),
    deleteDataRecord(),
    updateDataRecord(),
    checkUpdateDataRecordProgress(),
    moveDataRecordToDataRecordPreview()
  ]);
}
